import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';

import { firestore } from './firebase';

import type { StarredCard } from '@/types/lists';

/**
 * Toggle star status for a card. If currently starred, removes it.
 * If not starred, adds it with the current timestamp.
 */
export async function toggleStar(
  uid: string,
  oracleId: string,
  name: string,
  isCurrentlyStarred: boolean,
  scryfallId?: string,
): Promise<void> {
  const starRef = doc(firestore, 'users', uid, 'starred', oracleId);

  if (isCurrentlyStarred) {
    await deleteDoc(starRef);
  } else {
    const data: Record<string, unknown> = {
      oracle_id: oracleId,
      name,
      starred_at: serverTimestamp(),
    };
    if (scryfallId) {
      data.scryfall_id = scryfallId;
    }
    await setDoc(starRef, data);
  }
}

/**
 * Subscribe to real-time updates of starred cards for a user.
 * Returns an unsubscribe function.
 */
export function subscribeToStarred(uid: string, callback: (starred: Map<string, StarredCard>) => void): () => void {
  return onSnapshot(collection(firestore, 'users', uid, 'starred'), (snapshot) => {
    const map = new Map<string, StarredCard>();
    snapshot.forEach((doc) => {
      const data = doc.data();
      map.set(doc.id, {
        oracle_id: doc.id,
        name: data.name,
        scryfall_id: data.scryfall_id,
        starred_at: data.starred_at,
      });
    });
    callback(map);
  });
}

/**
 * Update the scryfall_id (display edition) for a starred card.
 */
export async function updateStarredEdition(uid: string, oracleId: string, scryfallId: string): Promise<void> {
  const starRef = doc(firestore, 'users', uid, 'starred', oracleId);
  await updateDoc(starRef, { scryfall_id: scryfallId });
}
